import * as React from 'react';
import type { SVGProps } from 'react';
import OtIcon from './OtIcon';

/**
 * Illustrated family — navigation group.
 * Lighting model and construction rules live in {@link OtIcon}.
 */

type TProps = SVGProps<SVGSVGElement>;

/** Key-light specular: a short arc, only where the surface faces the light. */
const Spec = ({ d, o = 0.92, w = 2.6 }: { d: string; o?: number; w?: number }) => (
	<path d={d} stroke='#fff' strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/** Bounce light coming back from below-right. */
const Bounce = ({ d, c, o = 0.5, w = 2 }: { d: string; c: string; o?: number; w?: number }) => (
	<path d={d} stroke={c} strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/* ------------------------------------------------------------------ Dashboard */
/** A gauge, not a tile grid: the literal cruscotto, and nothing else in the menu resembles it. */
export const OtDashboard = (props: TProps) => (
	<OtIcon
		slug='dashboard'
		palette='cyan'
		groundX={47}
		groundY={79}
		groundR={22}
		extraDefs={(refs, id) => (
			<>
				<linearGradient
					id={id('arc')}
					gradientUnits='userSpaceOnUse'
					x1='18'
					y1='56'
					x2='76'
					y2='26'>
					<stop offset='0' stopColor='#7fe6d2' />
					<stop offset='.5' stopColor='#8b7bea' />
					<stop offset='1' stopColor={refs.tone.light} />
				</linearGradient>
				<linearGradient
					id={id('face')}
					gradientUnits='userSpaceOnUse'
					x1='20'
					y1='24'
					x2='72'
					y2='60'>
					<stop offset='0' stopColor='var(--ot-icon-panel-a, #5b6280)' />
					<stop offset='.55' stopColor='var(--ot-icon-panel-b, #3a4056)' />
					<stop offset='1' stopColor='var(--ot-icon-panel-b, #232838)' />
				</linearGradient>
			</>
		)}
		{...props}>
		{(r) => (
			<>
				<path d='M18 56a29 29 0 0 1 58 0z' fill={r.fill} />
				<path
					d='M18 56a29 29 0 0 1 58 0'
					fill='none'
					stroke={r.tone.occlusion}
					strokeWidth='10'
					strokeLinecap='round'
				/>
				<path
					d='M18 56A29 29 0 0 1 62 31'
					fill='none'
					stroke='#fff'
					strokeWidth='10'
					strokeLinecap='round'
				/>
				<Spec d='M18 56A29 29 0 0 1 30 33' o={0.45} w={2.4} />
				{/* needle: its own shadow first, then the lit edge */}
				<path
					d='M47 56L64 36'
					stroke='#120f26'
					strokeOpacity='.5'
					strokeWidth='6.4'
					strokeLinecap='round'
				/>
				<path d='M46.2 55.2L63.2 35.2' stroke='#fff' strokeWidth='4.6' strokeLinecap='round' />
				<circle cx='47' cy='56' r='7.4' fill={r.body} />
				<circle cx='47' cy='56' r='7.4' fill='none' stroke={r.rim} strokeWidth='2.4' />
				<circle cx='47' cy='56' r='2.4' fill={r.tone.rimDark} />
				<Bounce d='M64 60a29 29 0 0 1-9 7' c={r.tone.bounce} o={0.55} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Search */
/** The lens over a person — the icon the whole family was derived from. */
export const OtSearch = (props: TProps) => (
	<OtIcon
		slug='search'
		palette='violet'
		groundX={52}
		groundY={83}
		groundR={13}
		extraDefs={(refs, id) => (
			<>
				<radialGradient id={id('glass')} gradientUnits='userSpaceOnUse' cx='37' cy='32' r='34'>
					<stop offset='0' stopColor='#a99bf8' stopOpacity='.62' />
					<stop offset='.55' stopColor='#5a4bb0' stopOpacity='.55' />
					<stop offset='1' stopColor='#1e1940' stopOpacity='.82' />
				</radialGradient>
				<linearGradient
					id={id('grip')}
					gradientUnits='userSpaceOnUse'
					x1='64'
					y1='60'
					x2='82'
					y2='78'>
					<stop offset='0' stopColor={refs.tone.rimDark} />
					<stop offset='.45' stopColor='#8b7bea' />
					<stop offset='1' stopColor='#4bb8a6' />
				</linearGradient>
			</>
		)}
		{...props}>
		{(r) => (
			<>
				<ellipse cx='79' cy='80' rx='8' ry='2.2' fill='#000' opacity='.5' filter={r.tight} />
				{/* handle read as a cylinder: dark base, lit body, highlight along the top edge */}
				<path d='M67 62l14 14' stroke='#1b1836' strokeWidth='10' strokeLinecap='round' />
				<path d='M67 62l14 14' stroke={r.own('grip')} strokeWidth='7.5' strokeLinecap='round' />
				<path
					d='M65.6 63.4l13.4 13.4'
					stroke='#d9d0ff'
					strokeOpacity='.72'
					strokeWidth='2.2'
					strokeLinecap='round'
				/>
				<circle cx='46' cy='42' r='24' fill={r.own('glass')} />
				<ellipse
					cx='35'
					cy='30'
					rx='13'
					ry='7.5'
					transform='rotate(-38 35 30)'
					fill='#fff'
					fillOpacity='.2'
				/>
				<circle cx='46' cy='35.5' r='5.9' fill={r.body} />
				<ellipse cx='46' cy='43.4' rx='7.4' ry='2.4' fill='#3d3468' opacity='.38' />
				<path
					d='M35 54c0-6.5 5.1-10.5 11-10.5S57 47.5 57 54c-3.2 2.7-7 4-11 4s-7.8-1.3-11-4z'
					fill={r.body}
				/>
				<circle cx='46' cy='42' r='24' fill='none' stroke={r.rim} strokeWidth='3.6' />
				<circle
					cx='46'
					cy='42'
					r='22.1'
					fill='none'
					stroke='#1e1a3c'
					strokeOpacity='.55'
					strokeWidth='1.5'
				/>
				<Spec d='M30 30a22.5 22.5 0 0 1 12.5-7.6' />
				<Bounce d='M62 58a22.5 22.5 0 0 1-9 6.5' c='#7fe6d2' o={0.55} />
				<path
					d='M77 22l1.7 4.1L83 27.8l-4.3 1.7L77 33.6l-1.7-4.1L71 27.8l4.3-1.7z'
					fill='#7fe6d2'
					filter={r.glow}
					opacity='.9'
				/>
				<path
					d='M77 22l1.7 4.1L83 27.8l-4.3 1.7L77 33.6l-1.7-4.1L71 27.8l4.3-1.7z'
					fill='#eafff9'
				/>
				<path
					d='M14 44l1.3 3.1 3.2 1.3-3.2 1.3L14 53l-1.3-3.3L9.5 48.4l3.2-1.3z'
					fill='#e6ddff'
					opacity='.9'
				/>
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ PersonSearch */
/** A person with the lens beside them — the search that starts from a name, not from a query. */
export const OtPersonSearch = (props: TProps) => (
	<OtIcon slug='person-search' palette='cyan' groundX={44} groundY={81} groundR={19} {...props}>
		{(r) => (
			<>
				<circle cx='40' cy='30' r='13' fill={r.fill} />
				<circle cx='40' cy='30' r='13' fill='none' stroke={r.rim} strokeWidth='3' />
				<ellipse cx='40' cy='44' rx='15' ry='3' fill={r.tone.occlusion} opacity='.4' />
				<path
					d='M18 68c0-12 10-20 22-20s22 8 22 20v4H18z'
					fill={r.fill}
					stroke={r.rim}
					strokeWidth='3'
					strokeLinejoin='round'
				/>
				<Spec d='M31 22a13.5 13.5 0 0 1 7.5-4.4' />
				<Bounce d='M56 60a19 19 0 0 1 2 8' c={r.tone.bounce} o={0.45} />
				{/* lens, in front */}
				<path d='M74 62l9 9' stroke='#0d2b27' strokeWidth='8' strokeLinecap='round' />
				<path d='M74 62l9 9' stroke={r.rim} strokeWidth='5.5' strokeLinecap='round' />
				<circle cx='66' cy='52' r='13' fill='#12332e' fillOpacity='.72' />
				<circle cx='66' cy='52' r='13' fill='none' stroke={r.rim} strokeWidth='3.2' />
				<Spec d='M58 46a12 12 0 0 1 6.5-4.2' o={0.85} w={2.2} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Work */
export const OtWork = (props: TProps) => (
	<OtIcon slug='work' palette='blue' groundX={48} groundY={81} groundR={20} {...props}>
		{(r) => (
			<>
				<path
					d='M37 24a7 7 0 0 1 7-7h8a7 7 0 0 1 7 7v5h-6v-4a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2v4h-6z'
					fill={r.body}
				/>
				<ellipse cx='48' cy='30' rx='13' ry='2.6' fill={r.tone.occlusion} opacity='.45' />
				<rect x='15' y='28' width='66' height='42' rx='11' fill={r.fill} />
				<rect
					x='15'
					y='28'
					width='66'
					height='42'
					rx='11'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
				/>
				<rect
					x='17.7'
					y='30.7'
					width='60.6'
					height='36.6'
					rx='8.8'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.38'
					strokeWidth='1.4'
				/>
				<rect x='15' y='44' width='66' height='7' fill='#fff' fillOpacity='.15' />
				<rect x='40' y='41' width='16' height='12' rx='4' fill={r.body} />
				<Spec d='M21 36a7 7 0 0 1 7-6h13' w={2.4} />
				<Bounce d='M75 61a7 7 0 0 1-6 5.8' c='#7fe6d2' o={0.55} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ BusinessCenter */
/**
 * Three cards in flight, staggered. No board behind them: a board is a container, and a container
 * at 24px reads as a box drawn around the icon. The cards are the object.
 *
 * Deliberately not a clipboard and not a briefcase: `OtAssignment` owns the clipboard and `OtWork`
 * owns the briefcase, and all three sit in the same menu.
 */
export const OtBusinessCenter = (props: TProps) => (
	<OtIcon slug='business-center' palette='indigo' groundX={47} groundY={80} groundR={26} {...props}>
		{(r) => (
			<>
				{/* back card, in shadow */}
				<rect x='16' y='30' width='26' height='34' rx='8' fill={r.fill} fillOpacity='.6' />
				<rect
					x='16'
					y='30'
					width='26'
					height='34'
					rx='8'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.6'
				/>
				{/* right card */}
				<rect x='54' y='22' width='26' height='42' rx='8' fill={r.fill} fillOpacity='.85' />
				<rect
					x='54'
					y='22'
					width='26'
					height='42'
					rx='8'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.6'
				/>
				{/* the front card lifts off the other two and drops onto them */}
				<ellipse
					cx='48'
					cy='70'
					rx='19'
					ry='4'
					fill={r.tone.occlusion}
					opacity='.5'
					filter={r.tight}
				/>
				<rect x='33' y='12' width='30' height='56' rx='9' fill={r.fill} />
				<rect
					x='33'
					y='12'
					width='30'
					height='56'
					rx='9'
					fill='none'
					stroke={r.rim}
					strokeWidth='3'
				/>
				<rect x='40' y='22' width='16' height='5' rx='2.5' fill='#fff' fillOpacity='.9' />
				<rect x='40' y='32' width='16' height='5' rx='2.5' fill='#fff' fillOpacity='.65' />
				<rect x='40' y='42' width='10' height='5' rx='2.5' fill='#fff' fillOpacity='.45' />
				<Spec d='M38 20a7 7 0 0 1 6-5' o={0.85} w={2.4} />
				<Bounce d='M76 54v8' c={r.tone.bounce} o={0.55} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Groups */
/** Three heads: the pool. */
export const OtGroups = (props: TProps) => (
	<OtIcon
		slug='groups'
		palette='orange'
		groundX={47}
		groundY={82}
		groundR={22}
		extraDefs={(refs, id) => (
			<>
				<linearGradient
					id={id('teal')}
					gradientUnits='userSpaceOnUse'
					x1='12'
					y1='32'
					x2='40'
					y2='60'>
					<stop offset='0' stopColor='#9ce9d4' />
					<stop offset='.5' stopColor='#46bcaa' />
					<stop offset='1' stopColor='#12433a' />
				</linearGradient>
				<linearGradient
					id={id('amber')}
					gradientUnits='userSpaceOnUse'
					x1='56'
					y1='32'
					x2='84'
					y2='60'>
					<stop offset='0' stopColor='#ffdf9a' />
					<stop offset='.5' stopColor='#e6a700' />
					<stop offset='1' stopColor='#5f3d04' />
				</linearGradient>
			</>
		)}
		{...props}>
		{(r) => (
			<>
				<circle cx='24' cy='45' r='15' fill={r.own('teal')} />
				<circle cx='24' cy='45' r='15' fill='none' stroke={r.rim} strokeWidth='2.4' />
				<circle cx='70' cy='45' r='15' fill={r.own('amber')} />
				<circle cx='70' cy='45' r='15' fill='none' stroke={r.rim} strokeWidth='2.4' />
				{/* the front head occludes the two behind it */}
				<ellipse
					cx='34'
					cy='47'
					rx='8'
					ry='12'
					fill={r.tone.occlusion}
					opacity='.3'
					filter={r.tight}
				/>
				<ellipse
					cx='60'
					cy='47'
					rx='8'
					ry='12'
					fill={r.tone.occlusion}
					opacity='.3'
					filter={r.tight}
				/>
				<circle cx='47' cy='42' r='23' fill={r.fill} />
				<circle cx='47' cy='42' r='23' fill='none' stroke={r.rim} strokeWidth='3.2' />
				<circle
					cx='47'
					cy='42'
					r='21.2'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.4'
					strokeWidth='1.4'
				/>
				<circle cx='47' cy='35' r='6.4' fill={r.body} />
				<ellipse cx='47' cy='43.6' rx='8' ry='2.5' fill='#2d2560' opacity='.4' />
				<path
					d='M35 55c0-6.9 5.5-11.2 12-11.2S59 48.1 59 55c-3.4 2.9-7.5 4.3-12 4.3s-8.6-1.4-12-4.3z'
					fill={r.body}
				/>
				<Spec d='M32 28a21.5 21.5 0 0 1 11.5-7' />
				<Bounce d='M63 57a21.5 21.5 0 0 1-8 6' c='#7fe6d2' />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ People */
/** Two heads: a pair, a relationship — not a pool. */
export const OtPeople = (props: TProps) => (
	<OtIcon slug='people' palette='orange' groundX={47} groundY={81} groundR={24} {...props}>
		{(r) => (
			<>
				<circle cx='66' cy='38' r='14' fill={r.fill} opacity='.9' />
				<circle cx='66' cy='38' r='14' fill='none' stroke={r.rim} strokeWidth='2.6' />
				<path
					d='M46 72c0-11 9-18 20-18s20 7 20 18z'
					fill={r.fill}
					opacity='.9'
					stroke={r.rim}
					strokeWidth='2.6'
					strokeLinejoin='round'
				/>
				<ellipse
					cx='50'
					cy='46'
					rx='9'
					ry='16'
					fill={r.tone.occlusion}
					opacity='.4'
					filter={r.tight}
				/>
				<circle cx='36' cy='34' r='16' fill={r.fill} />
				<circle cx='36' cy='34' r='16' fill='none' stroke={r.rim} strokeWidth='3' />
				<path
					d='M12 72c0-13 11-21 24-21s24 8 24 21z'
					fill={r.fill}
					stroke={r.rim}
					strokeWidth='3'
					strokeLinejoin='round'
				/>
				<Spec d='M27 26a15 15 0 0 1 8-5' />
				<Bounce d='M78 58a19 19 0 0 1 3 9' c={r.tone.bounce} o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ GroupWork */
/** Three interlocking discs — collaboration, not headcount. */
export const OtGroupWork = (props: TProps) => (
	<OtIcon
		slug='group-work'
		palette='pink'
		groundX={47}
		groundY={82}
		groundR={23}
		extraDefs={(refs, id) => (
			<>
				<linearGradient
					id={id('teal')}
					gradientUnits='userSpaceOnUse'
					x1='16'
					y1='40'
					x2='44'
					y2='72'>
					<stop offset='0' stopColor='#9ce9d4' />
					<stop offset='.5' stopColor='#46bcaa' />
					<stop offset='1' stopColor='#12433a' />
				</linearGradient>
			</>
		)}
		{...props}>
		{(r) => (
			<>
				<circle cx='47' cy='26' r='19' fill={r.fill} />
				<circle cx='47' cy='26' r='19' fill='none' stroke={r.rim} strokeWidth='3' />
				<ellipse
					cx='47'
					cy='45'
					rx='19'
					ry='6'
					fill={r.tone.occlusion}
					opacity='.42'
					filter={r.tight}
				/>
				<circle cx='30' cy='56' r='19' fill={r.own('teal')} />
				<circle cx='30' cy='56' r='19' fill='none' stroke={r.rim} strokeWidth='3' />
				<circle cx='64' cy='56' r='19' fill={r.fill} opacity='.92' />
				<circle cx='64' cy='56' r='19' fill='none' stroke={r.rim} strokeWidth='3' />
				<circle cx='47' cy='26' r='7.5' fill={r.body} />
				<circle cx='30' cy='56' r='7.5' fill={r.body} />
				<circle cx='64' cy='56' r='7.5' fill={r.body} fillOpacity='.85' />
				<Spec d='M36 18a18 18 0 0 1 9-5.4' />
				<Bounce d='M78 50a18 18 0 0 1 1 8' c='#7fe6d2' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Person */
export const OtPerson = (props: TProps) => (
	<OtIcon slug='person' palette='orange' groundX={47} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='47' cy='30' r='16' fill={r.fill} />
				<circle cx='47' cy='30' r='16' fill='none' stroke={r.rim} strokeWidth='3.2' />
				<ellipse cx='47' cy='47' rx='18' ry='3.4' fill={r.tone.occlusion} opacity='.42' />
				<path
					d='M17 72c0-14 13-23 30-23s30 9 30 23z'
					fill={r.fill}
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				<Spec d='M37 22a15.5 15.5 0 0 1 8.5-5' />
				<Bounce d='M69 60a20 20 0 0 1 2.5 8' c={r.tone.bounce} o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ FolderSpecial */
export const OtFolderSpecial = (props: TProps) => (
	<OtIcon slug='folder-special' palette='gold' groundX={48} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<path
					d='M12 26a8 8 0 0 1 8-8h16l7 8h33a8 8 0 0 1 8 8v6H12z'
					fill={r.fill}
					opacity='.85'
				/>
				<rect x='12' y='32' width='72' height='40' rx='10' fill={r.fill} />
				<rect
					x='12'
					y='32'
					width='72'
					height='40'
					rx='10'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				<rect
					x='14.7'
					y='34.7'
					width='66.6'
					height='34.6'
					rx='7.8'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.35'
					strokeWidth='1.4'
				/>
				<path d='M48 40l4.4 9.2 10 1.4-7.2 7 1.7 10-8.9-4.8-8.9 4.8 1.7-10-7.2-7 10-1.4z' fill={r.body} />
				<Spec d='M19 40a8 8 0 0 1 8-6.4h12' w={2.4} />
				<Bounce d='M79 62a8 8 0 0 1-6 6' c='#7fe6d2' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ AutoAwesome */
export const OtAutoAwesome = (props: TProps) => (
	<OtIcon slug='auto-awesome' palette='violet' groundX={46} groundY={80} groundR={16} {...props}>
		{(r) => (
			<>
				<path d='M46 12l7.5 20.5L74 40l-20.5 7.5L46 68l-7.5-20.5L18 40l20.5-7.5z' fill={r.fill} />
				<path
					d='M46 12l7.5 20.5L74 40l-20.5 7.5L46 68l-7.5-20.5L18 40l20.5-7.5z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				<path d='M46 12l7.5 20.5L46 40z' fill='#fff' fillOpacity='.28' />
				<path d='M46 68l7.5-20.5L46 40z' fill={r.tone.occlusion} fillOpacity='.32' />
				<Spec d='M36 30a14 14 0 0 1 7.5-8.6' o={0.9} />
				<path
					d='M76 52l1.6 4 4 1.6-4 1.6L76 63l-1.6-3.8-4-1.6 4-1.6z'
					fill='#7fe6d2'
					filter={r.glow}
					opacity='.85'
				/>
				<path d='M76 52l1.6 4 4 1.6-4 1.6L76 63l-1.6-3.8-4-1.6 4-1.6z' fill='#eafff9' />
				<circle cx='18' cy='22' r='2.6' fill='#ffd97a' />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Favorite */
export const OtFavorite = (props: TProps) => (
	<OtIcon slug='favorite' palette='pink' groundX={46} groundY={80} groundR={19} {...props}>
		{(r) => (
			<>
				<path
					d='M46 71C25 55 15 47 15 35a17 17 0 0 1 31-9 17 17 0 0 1 31 9c0 12-10 20-31 36z'
					fill={r.fill}
				/>
				<path
					d='M46 71C25 55 15 47 15 35a17 17 0 0 1 31-9 17 17 0 0 1 31 9c0 12-10 20-31 36z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				<Spec d='M27 34a10.5 10.5 0 0 1 8.5-10' o={0.95} w={3.2} />
				<Bounce d='M67 45a12 12 0 0 1-7 9' c='#ffb3d8' o={0.45} w={2.2} />
				<path
					d='M79 20l1.6 3.9 3.9 1.6-3.9 1.6L79 31l-1.6-3.9-3.9-1.6 3.9-1.6z'
					fill='#a78bfa'
					filter={r.glow}
					opacity='.8'
				/>
				<path
					d='M79 20l1.6 3.9 3.9 1.6-3.9 1.6L79 31l-1.6-3.9-3.9-1.6 3.9-1.6z'
					fill='#e6ddff'
				/>
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Insights */
/** Three bars and the line through them. No panel behind: the bars are the object. */
export const OtInsights = (props: TProps) => (
	<OtIcon slug='insights' palette='cyan' groundX={46} groundY={80} groundR={26} {...props}>
		{(r) => (
			<>
				<rect x='14' y='46' width='17' height='28' rx='7' fill={r.fill} fillOpacity='.7' />
				<rect x='14' y='46' width='17' height='28' rx='7' fill='none' stroke={r.rim} strokeWidth='2.6' />
				<rect x='37' y='32' width='17' height='42' rx='7' fill={r.fill} />
				<rect x='37' y='32' width='17' height='42' rx='7' fill='none' stroke={r.rim} strokeWidth='2.8' />
				<rect x='60' y='16' width='17' height='58' rx='7' fill={r.fill} />
				<rect x='60' y='16' width='17' height='58' rx='7' fill='none' stroke={r.rim} strokeWidth='3' />
				{/* the trend, engraved across the bars */}
				<path
					d='M22 40l23-14 23-16'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='8'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<path
					d='M21 39l23-14 23-16'
					fill='none'
					stroke='#fff'
					strokeWidth='5'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<circle cx='68' cy='9' r='6' fill='#fff' />
				<Spec d='M19 52a7 7 0 0 1 6-4' o={0.85} w={2.4} />
				<Bounce d='M74 62v8' c={r.tone.bounce} o={0.55} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ TrackChanges */
export const OtTrackChanges = (props: TProps) => (
	<OtIcon slug='track-changes' palette='pink' groundX={46} groundY={81} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='30' fill={r.fill} opacity='.28' />
				<circle cx='46' cy='42' r='30' fill='none' stroke={r.rim} strokeWidth='3.2' />
				<circle cx='46' cy='42' r='19' fill={r.fill} opacity='.5' />
				<circle cx='46' cy='42' r='19' fill='none' stroke={r.rim} strokeWidth='2.6' />
				<circle cx='46' cy='42' r='8' fill={r.fill} />
				<circle cx='46' cy='42' r='8' fill='none' stroke={r.rim} strokeWidth='2.4' />
				<circle cx='46' cy='42' r='3' fill='#fff' />
				{/* the arrow that breaks the rings, so it reads as a target and not as a ripple */}
				<path
					d='M52 36l22-22'
					stroke={r.tone.occlusion}
					strokeOpacity='.5'
					strokeWidth='7'
					strokeLinecap='round'
				/>
				<path d='M51 35l22-22' stroke='#fff' strokeWidth='4.4' strokeLinecap='round' />
				<path d='M64 12h12v12' fill='none' stroke='#fff' strokeWidth='4.4' strokeLinecap='round' strokeLinejoin='round' />
				<Spec d='M28 28a26 26 0 0 1 12-8.6' o={0.85} />
				<Bounce d='M66 62a26 26 0 0 1-10 7' c='#7fe6d2' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Visibility */
export const OtVisibility = (props: TProps) => (
	<OtIcon
		slug='visibility'
		palette='cyan'
		groundX={47}
		groundY={76}
		groundR={26}
		extraDefs={(refs, id) => (
			<radialGradient id={id('iris')} gradientUnits='userSpaceOnUse' cx='41' cy='36' r='20'>
				<stop offset='0' stopColor='#a99bf8' />
				<stop offset='.55' stopColor='#5a4bb0' />
				<stop offset='1' stopColor='#1e1940' />
			</radialGradient>
		)}
		{...props}>
		{(r) => (
			<>
				<path d='M8 42c9-15 22-23 39-23s30 8 39 23c-9 15-22 23-39 23S17 57 8 42z' fill={r.fill} />
				<path
					d='M8 42c9-15 22-23 39-23s30 8 39 23c-9 15-22 23-39 23S17 57 8 42z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				<circle cx='47' cy='42' r='17' fill={r.own('iris')} />
				<circle cx='47' cy='42' r='17' fill='none' stroke={r.rim} strokeWidth='2.6' />
				<circle cx='47' cy='42' r='7' fill='#12102b' />
				<circle cx='41' cy='35' r='4.6' fill='#fff' fillOpacity='.9' />
				<circle cx='53' cy='49' r='2.4' fill='#7fe6d2' fillOpacity='.55' />
				<Spec d='M20 36a34 34 0 0 1 14-10' o={0.7} w={2.4} />
				<Bounce d='M74 50a34 34 0 0 1-12 9' c='#7fe6d2' o={0.5} />
			</>
		)}
	</OtIcon>
);
